import React, { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { UseCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import loadingSpinner from '../../../img/loading-spinner.gif';
import './CartPreview.scss';
import { useValueChangeEffect } from '../../shared/hooks/useValueChangeEffect';
import CSSTransition from 'react-transition-group/CSSTransition';
import { LoanFormatIcon } from '../../shared/common/GildIcon';
import FormatCurrency from '../../shared/utils/formatCurrency';
import { useTranslation } from 'react-i18next';

type Props = {
    cartSummaryQuery: UseCartSummaryQuery;
};

export const CartPreview: FC<Props> = ({ cartSummaryQuery }) => {
    const { t } = useTranslation();
    const [bounce, setBounce] = useState(false);
    const { status, data } = cartSummaryQuery;

    useValueChangeEffect<number | undefined>({
        effect: useCallback(() => {
            setBounce(true);
            const timeout = setTimeout(() => setBounce(false), 1000);
            return () => clearTimeout(timeout);
        }, []),
        observedValue:
            status !== 'success' ? undefined : data.eaudiobooksAmount + data.ebooksAmount + data.epressAmount,
        skipInitial: true
    });

    if (status === 'loading') {
        return (
            <div className={classNames('quick-cart')} data-displayname="CartPreview" data-testid="CartPreview">
                <img className="loading" src={loadingSpinner} />
            </div>
        );
    }

    if (status === 'error') {
        return null;
    }

    const isEmpty = data.ebooksAmount === 0 && data.eaudiobooksAmount === 0 && data.epressAmount === 0;

    return (
        <div
            className={classNames('quick-cart', isEmpty && 'empty')}
            data-displayname="CartPreview"
            aria-label="Cart summary"
        >
            <CSSTransition timeout={1000} classNames="cart-transition" in={bounce} addEndListener={() => {}}>
                <Link to="/cart" className="display-flex" data-testid="cart-detail-link">
                    <i className="material-icons">shopping_cart</i>
                    {isEmpty && (
                        <span style={{ marginLeft: '5px' }}>
                            {t('numberFormat.numberWithCurrencySymbol', {
                                val: FormatCurrency.convertIntegerToCurrencyFormat(data.totalNetValue),
                                currencySymbol: FormatCurrency.mapCurrencyToSymbol(data.currency)
                            })}
                        </span>
                    )}
                    {!isEmpty && (
                        <div>
                            <div className="quick-cart-totals">
                                {data.ebooksAmount > 0 && (
                                    <span className="display-inline-flex">
                                        <LoanFormatIcon icon="ebook-filled" className="loanformat-icon-base" />
                                        <span>{data.ebooksAmount}</span>
                                    </span>
                                )}
                                {data.eaudiobooksAmount > 0 && (
                                    <span className="display-inline-flex">
                                        <LoanFormatIcon icon="eaudiobook-filled" className="loanformat-icon-base" />
                                        <span>{data.eaudiobooksAmount}</span>
                                    </span>
                                )}
                                {data.epressAmount > 0 && (
                                    <span className="display-inline-flex">
                                        <LoanFormatIcon icon="emagazine-filled" className="loanformat-icon-base" />
                                        <span>{data.epressAmount}</span>
                                    </span>
                                )}
                            </div>
                            <div>
                                {t('numberFormat.numberWithCurrencySymbol', {
                                    val: FormatCurrency.convertIntegerToCurrencyFormat(data.totalNetValue),
                                    currencySymbol: FormatCurrency.mapCurrencyToSymbol(data.currency)
                                })}
                            </div>
                        </div>
                    )}
                </Link>
            </CSSTransition>
        </div>
    );
};
